import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useEndpointFetch } from '../../../global/network/useEndpointFetch';
import { VolunteerCommonProfileDialog } from '../volunteer-common-profile/VolunteerCommonProfileDialog';
import { CommunicationLogRow } from './CommunicationLogRow';
import { CommunicationsPagination, PAGE_SIZE_OPTIONS } from './CommunicationsPagination';
import { CommunicationsToolbar } from './CommunicationsToolbar';
import { SentMessagePanel } from './SentMessagePanel';
import { OpenProfileRequest } from './InboxThreadPanel';
import {
  CommunicationLogItem,
  CommunicationsPerson,
  GetRosterSmsRequest,
  GetRosterSmsResponse,
  GetSentRequest,
  GetSentResponse,
  RosterSms,
  LookupProfilesRequest,
  LookupProfilesResponse,
  mergeCommunicationLog,
  CHANNEL_OPTIONS,
  TYPE_OPTIONS,
} from './communications-model';

const rosterPerson = (roster: RosterSms): CommunicationsPerson => {
  const [preferredName, ...rest] = roster.volunteerName.split(' ');
  return {
    volunteerId: roster.volunteerId,
    profileId: roster.volunteerProfileId,
    userId: roster.volunteerUserId,
    firstName: preferredName,
    preferredName,
    lastName: rest.join(' '),
    avatarUrl: roster.volunteerAvatarUrl,
  };
};

interface CommunicationLogScreenProps {
  title: string;
  emptyTitle: string;
  emptyDescription: string;
  refreshKey?: number;
}

export const CommunicationLogScreen = ({
  title,
  emptyTitle,
  emptyDescription,
  refreshKey,
}: CommunicationLogScreenProps) => {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [channel, setChannel] = useState('all');
  const [type, setType] = useState('all');
  const [sentFrom, setSentFrom] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<CommunicationLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [openProfile, setOpenProfile] = useState<OpenProfileRequest>();
  const [openItem, setOpenItem] = useState<CommunicationLogItem>();
  const [sendersById, setSendersById] = useState<Record<string, CommunicationsPerson>>({});
  const [recipientsByUserId, setRecipientsByUserId] = useState<Record<string, CommunicationsPerson>>({});
  const latestRequest = useRef(0);

  const getSent = useEndpointFetch<GetSentRequest, GetSentResponse>('/em/comms/sent');
  const getRosterSms = useEndpointFetch<GetRosterSmsRequest, GetRosterSmsResponse>('/vm/comms/roster-sms');
  const lookupProfiles = useEndpointFetch<LookupProfilesRequest, LookupProfilesResponse>('/vm/comms/lookup-profiles');

  const loadPeople = async (logItems: CommunicationLogItem[], requestId: number) => {
    const unique = (values: string[]) => values.filter((v, index, all) => all.indexOf(v) === index);

    const profileIds = unique(
      logItems
        .map((item) => (item.kind === 'sent' ? item.sent.sentBy : null))
        .filter((sentBy): sentBy is string => Boolean(sentBy))
    );

    const userIds = unique(
      logItems.reduce<string[]>(
        (acc, item) => (item.kind === 'sent' ? [...acc, ...item.sent.recipientUserIds] : acc),
        []
      )
    );

    if (profileIds.length === 0 && userIds.length === 0) {
      setSendersById({});
      setRecipientsByUserId({});
      return;
    }

    const response = await lookupProfiles.run({ profileIds, userIds });
    if (requestId !== latestRequest.current) return;
    const ok = response && response.ok;
    setSendersById(ok ? response.data.data?.profilesById ?? {} : {});
    setRecipientsByUserId(ok ? response.data.data?.profilesByUserId ?? {} : {});
  };

  const itemKey = (logItem: CommunicationLogItem) =>
    logItem.kind === 'sent' ? logItem.sent.groupKey : logItem.roster.smsLogId;

  const recipientsFor = (logItem: CommunicationLogItem): CommunicationsPerson[] =>
    logItem.kind === 'roster'
      ? [rosterPerson(logItem.roster)]
      : logItem.sent.recipientUserIds
          .map((userId) => recipientsByUserId[userId])
          .filter((person): person is CommunicationsPerson => Boolean(person));

  const senderFor = (logItem: CommunicationLogItem): CommunicationsPerson | undefined =>
    logItem.kind === 'sent' && logItem.sent.sentBy ? sendersById[logItem.sent.sentBy] : undefined;

  const load = useCallback(async () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    setLoading(true);
    try {
      // Both sources are paged from the start and windowed after the merge. Paging each source by
      // the same offset independently drops rows that lost the merge on earlier pages.
      const windowEnd = offset + pageSize;
      const includeRoster = channel !== 'email' && type !== 'manual';

      const sentResponse = await getSent.run({
        channel: channel === 'all' ? undefined : (channel as 'sms' | 'email'),
        search: appliedSearch || undefined,
        automated: type === 'all' ? undefined : type === 'automated',
        sentFrom: sentFrom ?? undefined,
        sentTo: sentTo ?? undefined,
        offset: 0,
        limit: windowEnd,
      });

      // Roster reminders live in vm_db, so they are a separate query merged here rather than
      // a cross-schema join.
      const rosterResponse = includeRoster
        ? await getRosterSms.run({
            direction: 'sent',
            search: appliedSearch || undefined,
            sentFrom: sentFrom ?? undefined,
            sentTo: sentTo ?? undefined,
            offset: 0,
            limit: windowEnd,
          })
        : undefined;

      if (requestId !== latestRequest.current) return;

      const sent = sentResponse && sentResponse.ok ? sentResponse.data.data?.sent ?? [] : [];
      const rosterSms = rosterResponse && rosterResponse.ok ? rosterResponse.data.data?.rosterSms ?? [] : [];

      const sentTotal = sentResponse && sentResponse.ok ? sentResponse.data.data?.pagination.total ?? 0 : 0;
      const rosterTotal = rosterResponse && rosterResponse.ok ? rosterResponse.data.data?.pagination.total ?? 0 : 0;

      const merged = mergeCommunicationLog(sent, rosterSms).slice(offset, windowEnd);
      setItems(merged);
      setTotal(sentTotal + rosterTotal);
      await loadPeople(merged, requestId);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [appliedSearch, channel, type, sentFrom, sentTo, offset, pageSize, refreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    setOffset(0);
    setAppliedSearch(search);
  };

  return (
    <Box display='flex' flexDirection='column' flex={1} minHeight={0}>
      <CommunicationsToolbar
        title={title}
        search={search}
        searchPlaceholder='Search messages'
        onSearchChange={setSearch}
        onSearch={applySearch}
        filters={[
          {
            key: 'channel',
            options: CHANNEL_OPTIONS,
            value: channel,
            onChange: (value) => {
              setOffset(0);
              setChannel(value);
            },
          },
          {
            key: 'type',
            options: TYPE_OPTIONS,
            value: type,
            onChange: (value) => {
              setOffset(0);
              setType(value);
            },
          },
        ]}
        dateRange={{
          from: sentFrom,
          to: sentTo,
          label: 'Sent',
          onChange: (from, to) => {
            setOffset(0);
            setSentFrom(from);
            setSentTo(to);
          },
        }}
      />

      <Box flexGrow={1} display='flex' minHeight={0}>
        <Box flexGrow={1} minWidth={0} style={{ overflowY: 'auto' }}>
          {loading && (
            <Box display='flex' justifyContent='center' padding='40px'>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && items.length === 0 && (
            <Box display='flex' flexDirection='column' alignItems='center' padding='64px 24px'>
              <Typography style={{ fontSize: 16, fontWeight: 600, color: '#444444' }}>{emptyTitle}</Typography>
              <Typography style={{ fontSize: 14, color: '#646464', marginTop: 8, textAlign: 'center', maxWidth: 420 }}>
                {emptyDescription}
              </Typography>
            </Box>
          )}

          {!loading &&
            items.map((item) => (
              <CommunicationLogRow
                key={itemKey(item)}
                item={item}
                sender={senderFor(item)}
                recipients={recipientsFor(item)}
                onOpen={setOpenItem}
                selected={Boolean(openItem) && itemKey(item) === itemKey(openItem as CommunicationLogItem)}
              />
            ))}
        </Box>

        {openItem && (
          <SentMessagePanel
            key={itemKey(openItem)}
            item={openItem}
            sender={senderFor(openItem)}
            recipients={recipientsFor(openItem)}
            onClose={() => setOpenItem(undefined)}
            onOpenProfile={setOpenProfile}
          />
        )}
      </Box>

      <CommunicationsPagination
        total={total}
        offset={offset}
        pageSize={pageSize}
        onOffsetChange={setOffset}
        onPageSizeChange={setPageSize}
      />

      {openProfile && (
        <VolunteerCommonProfileDialog
          key={openProfile.profileId}
          profileId={openProfile.profileId}
          userId={openProfile.userId}
          openOn={openProfile.openOn}
          open
          onClose={() => setOpenProfile(undefined)}
        />
      )}
    </Box>
  );
};
